/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useScorecardData, useUserSearch } from 'hooks';
import { getUserPosition } from 'helpers';
import { commonTexts } from 'i18n';
import { User } from 'store/interfaces';
import UserListItem from 'components/UserListItem';
import DialogEmptyState from 'components/DialogEmptyState';
import { DialogTitle, DialogActions } from '../Dialog';
import {
  Dialog,
  DialogContent,
  SearchWrapper,
} from './UserSelectDialog.styled';
import { UserSelectDialogProps } from './interfaces';
import SearchBox from '../SearchBox/SearchBox.component';
import SuggestedUsers from './SuggestedUsers.component';
import { isValidEmail } from '../../helpers/validation/isValidEmail';
import ReviewersList from './ReviewersList.component';

import messages from './messages';
import NoOptions from './NoOptions/NoOptions.component';

export default function UserSelectDialog({
  title,
  searchBoxLabel,
  searchBoxPlaceholder,
  listLabel,
  submitButtonText,
  cancelButtonText,
  children,
  initialUsers = [],
  isSubmitting,
  isRequired = false,
  excludeIds,
  enableSelfAssessment,
  autoAssignPmReviewer,
  limitReviewersToEmployeeAndPm,
  lockedUserIds,
  requireNewUsers,
  listLabelRight,
  RowSecondaryAction,
  PrimarySuffix,
  maxPaperWidth,
  paperHeight,
  aboveSearch,
  resetKey,
  onRemove,
  onUsersChange,
  onSubmit,
  onClose,
}: UserSelectDialogProps) {
  const [users, setUsers] = useState(initialUsers);
  const [searchText, setSearchText] = useState<string | null>('');
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setUsers(initialUsers);
    setSearchText('');
  }
  const { search } = useUserSearch(false, true);
  const { workRecord, careerMentor } = useScorecardData();
  const { user: scorecardOwner } = workRecord;

  const onSearch = (query: string) => search({ searchText: query, excludeIds });

  const matchesEmail = (user: User, text: string) =>
    Boolean(user.email) &&
    user.email?.trim().toLowerCase() === text.trim().toLowerCase();

  /**
   * Offer to invite an address only when it is a valid email that is not
   * already in the list below. A reviewer added earlier in this session may not
   * come back from the search at all — a brand new external user does not exist
   * server-side yet, and `excludeIds` can hide an existing one — so the
   * selected list has to be checked here rather than left to the search result.
   */
  const canInviteEmail = (text: string) =>
    isValidEmail(text) && !users.some((user) => matchesEmail(user, text));

  const filterUsers = useCallback(
    (userOptions: User[]) =>
      limitReviewersToEmployeeAndPm
        ? userOptions.filter(
            ({ id }) =>
              id === scorecardOwner.id ||
              (careerMentor && id === careerMentor.id),
          )
        : userOptions.filter(({ id }) => !users.find((user) => user.id === id)),
    [users, limitReviewersToEmployeeAndPm, scorecardOwner.id, careerMentor],
  );

  const applyUsers = (next: User[]) => {
    setUsers(next);
    onUsersChange?.(next);
  };

  const addUser = (value: unknown) => {
    const userToAdd = value as User;

    if (!userToAdd) return;

    if (!users.find((user) => user.id === userToAdd.id)) {
      applyUsers([...users, userToAdd]);
    }
  };

  const addAllSuggestedUsers = (suggestedUsers: User[]) => {
    const usersToAdd = suggestedUsers.filter(
      (user) => !users.find(({ id }) => id === user.id),
    );
    applyUsers([...users, ...usersToAdd]);
  };

  const removeUser = (userToRemove: User) => {
    applyUsers(users.filter(({ id }) => id !== userToRemove.id));
    if (onRemove) {
      onRemove(userToRemove);
    }
  };

  const hasNewUsers = (() => {
    const lockedSet = new Set<number | string>(lockedUserIds ?? []);
    return users.some((user) => !lockedSet.has(user.id as number));
  })();

  const submitDisabled = requireNewUsers
    ? !hasNewUsers
    : !users.length && users.length === initialUsers?.length;

  const submit = () => {
    onSubmit({
      users: users.filter((user) => !user.isNewExternalUser),
      newExternalUserEmails: users
        .filter((user) => user.isNewExternalUser)
        .map((user) => user.email),
    });
  };

  const handleNewUserCreate = () => {
    addUser({ id: searchText, email: searchText, isNewExternalUser: true });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      PaperProps={
        maxPaperWidth !== undefined || paperHeight !== undefined
          ? {
              sx: {
                ...(maxPaperWidth !== undefined && {
                  maxWidth: `${maxPaperWidth}${
                    typeof maxPaperWidth === 'number' ? 'px' : ''
                  } !important`,
                }),
                ...(paperHeight !== undefined && {
                  height: `${paperHeight}${
                    typeof paperHeight === 'number' ? 'px' : ''
                  }`,
                  maxHeight: `${paperHeight}${
                    typeof paperHeight === 'number' ? 'px' : ''
                  }`,
                }),
              },
            }
          : undefined
      }
    >
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <DialogContent id="setReviewersModal">
        {aboveSearch && <Box sx={{ marginBottom: 1 }}>{aboveSearch}</Box>}
        <SearchWrapper>
          <SearchBox<User>
            key={users?.map((i) => i.id).join(',')}
            isRequired={isRequired}
            searchLabel={searchBoxLabel}
            searchPlaceholder={searchBoxPlaceholder}
            onItemSelect={addUser}
            filterOptions={filterUsers}
            getOptionLabel={(user) =>
              typeof user === 'string' ? user : user.fullName
            }
            renderOption={(props, user) => (
              <UserListItem
                key={`search-option-${user.email}`}
                {...props}
                recipient={{
                  user,
                  position: { id: 0, name: getUserPosition(user) },
                }}
              />
            )}
            noOptionsComponent={() => (
              <NoOptions
                externalEmail={searchText}
                onExternalUserInvite={handleNewUserCreate}
              />
            )}
            onSearch={onSearch}
            noOptionsDisplayRule={canInviteEmail}
            isExactMatch={matchesEmail}
            onSearchChange={setSearchText}
          />
        </SearchWrapper>
        {listLabelRight ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 2,
              marginBottom: '16px',
            }}
          >
            <Typography variant="body4" component="span">
              {listLabel}
            </Typography>
            <Typography
              variant="body4"
              component="span"
              sx={{ color: '#6E7071' }}
            >
              {listLabelRight}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ marginBottom: '16px' }} variant="body4">
            {listLabel}
          </Typography>
        )}
        <ReviewersList
          enableSelfAssessment={!!enableSelfAssessment}
          autoAssignPmReviewer={!!autoAssignPmReviewer}
          scorecardOwner={scorecardOwner}
          users={users}
          workRecord={workRecord}
          careerMentor={careerMentor}
          lockedUserIds={lockedUserIds}
          RowSecondaryAction={RowSecondaryAction}
          PrimarySuffix={PrimarySuffix}
          onRemoveUser={removeUser}
        />
        {!users.length && !enableSelfAssessment && !autoAssignPmReviewer && (
          <DialogEmptyState
            title={<FormattedMessage {...messages.addReviewers} />}
          />
        )}
        <SuggestedUsers
          selectedUsers={users}
          addUser={addUser}
          addAllSuggestedUsers={addAllSuggestedUsers}
        />
        {children}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          {cancelButtonText || <FormattedMessage {...commonTexts.cancel} />}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          disabled={submitDisabled}
          onClick={submit}
        >
          {submitButtonText || <FormattedMessage {...commonTexts.submit} />}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
